import { Box, Link, List, ListItem, useMediaQuery } from '@mui/material';
import './directIntegrationTab.scss';
import { useTranslation } from 'react-i18next';
import { Container } from '@mui/system';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import CardMedia from '@mui/material/CardMedia';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import heroCard from '../../assets/images/directIntegration/heroCard.jpg';
import Check from '../../assets/images/directIntegration/checkmark.png';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const DirectIntegrationTab = ({ jsonData }) => {
  const { t, i18n } = useTranslation();
  const json = t('directIntegration', { returnObjects: true });
  const navigate = useNavigate();
  const theme = useTheme();
  // Treat widths between small and large as tablet (covers iPad Pro portrait)
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  const handleRegisterClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${i18n.language}/api/gettingstarted/gettingstarted`);
  };

  const handleLearnClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${i18n.language}/ndc-program/ndc-capabilities`);
  };

  const handleQuickLinks = (links, download) => {
    if (download) {
      window.open(links, '_blank');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/${i18n.language}${links}`);
    }
  };

  return (
    <>
      <Grid container justifyContent={'center'}>
        <Grid
          item
          sx={{
            width: '100%',
            marginX: '32px',
            maxWidth: '1200px',
            '@media (max-width: 900px)': {
              maxWidth: '704px'
            }
          }}
        >
          <Grid container rowSpacing={5}>
            <Grid item>
              <section>
                <Box
                  sx={{
                    mt: { xs: 2, sm: 3, md: 4 },
                    paddingX: '0px'
                  }}
                >
                  <Box sx={{ mb: { xs: 3, md: 4 } }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '28px', sm: '28px', md: '28px' },
                        lineHeight: { xs: '36px', sm: '36px', md: '36px' },
                        color: '#000000',
                        mb: 0
                      }}
                    >
                      {json['heading']}
                    </Typography>
                  </Box>
                  <Grid
                    container
                    justifyContent="space-between"
                    sx={{
                      '@media (max-width: 1200px)': {
                        rowGap: 5
                      }
                    }}
                  >
                    {/* Left: Access Content */}
                    <Grid
                      item
                      xs={12}
                      md={isTablet ? 12 : 8}
                      sx={{
                        display: 'flex'
                      }}
                    >
                      <Box
                        sx={{
                          boxShadow: '0px 0px 15px 0px #00000026 !important',
                          borderRadius: '14px',
                          width: '100%',
                          p: { xs: 2, sm: 2.5, md: 3 },
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: isTablet ? 3 : { xs: 2, md: 3 },
                          mb: { xs: 2, md: 0 }
                        }}
                      >
                        <Box
                          sx={{
                            flex: '0 0 auto',
                            width: isTablet
                              ? '50%'
                              : { xs: '100%', sm: '40%', md: '45%' }
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={heroCard}
                            alt={'Golden Gate Bridge with Air Canada branding'}
                            role="img"
                            sx={{
                              borderRadius: '16px',
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div>
                            <Typography
                              component="h3"
                              variant="h3"
                              className="hero-card__heading"
                              sx={{
                                color: '#000000',
                                fontWeight: 600,
                                fontSize: {
                                  xs: '18px',
                                  sm: '19px',
                                  md: '20px'
                                },
                                lineHeight: {
                                  xs: '26px',
                                  sm: '27px',
                                  md: '28px'
                                },
                                mb: { xs: 2.5, sm: 2, md: 3 }
                              }}
                            >
                              {json['heroCard']?.heading}
                            </Typography>
                            {(json['heroCard'].content ?? '')
                              .split('\n')

                              .map((paragraph: string, idx: number) => (
                                <Typography
                                  key={idx}
                                  variant="body2"
                                  sx={{
                                    mt: 0,
                                    mb: { xs: 3, md: 0.5 },
                                    lineHeight: {
                                      xs: '24px',
                                      md: '22px',
                                      fontWeight: 400,
                                      fontSize: '14px',
                                      lineHeight: '20px'
                                    }
                                  }}
                                >
                                  {paragraph}
                                </Typography>
                              ))}
                          </div>

                          <Button
                            variant="primary"
                            size="xl"
                            aria-label="Learn about our capabilities"
                            onClick={handleLearnClick}
                            endIcon={<NavigateNextRoundedIcon />}
                          >
                            {json['learn']}
                          </Button>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Right: Info + Links */}
                    <Grid
                      item
                      xs={12}
                      md={isTablet ? 12 : 3.8}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: '#F1F7FB',
                          borderRadius: '16px',
                          p: { xs: 2, sm: 2.5, md: 3 },
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        className="side-info"
                      >
                        {/* Agency Info */}
                        <Box sx={{ mb: 2.5 }}>
                          <Typography
                            component="h3"
                            variant="h4"
                            mb={1.5}
                            sx={{
                              color: '#003D78',
                              fontWeight: 600,
                              fontSize: { xs: '18px', sm: '19px', md: '20px' },
                              lineHeight: { xs: '26px', sm: '27px', md: '28px' }
                            }}
                          >
                            {json['access']['subHeading']}
                          </Typography>

                          <ul
                            style={{ listStyle: 'none', margin: 0, padding: 0 }}
                          >
                            {json['access']['agency'].map((item, index) => (
                              <li key={index} style={{ marginBottom: '8px' }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 400,
                                    fontSize: {
                                      xs: '14px',
                                      sm: '15px',
                                      md: '16px'
                                    },
                                    lineHeight: {
                                      xs: '20px',
                                      sm: '22px',
                                      md: '24px'
                                    },
                                    color: '#000000',
                                    display: 'flex',
                                    alignItems: 'flex-start'
                                  }}
                                >
                                  <img
                                    src={Check}
                                    alt=""
                                    role="presentation"
                                    style={{
                                      width: '16px',
                                      height: '12px',
                                      marginRight: '12px',
                                      marginTop: '6px',
                                      flexShrink: 0
                                    }}
                                  />
                                  <span
                                    style={{
                                      position: 'absolute',
                                      width: '1px',
                                      height: '1px',
                                      padding: 0,
                                      margin: '-1px',
                                      overflow: 'hidden',
                                      clip: 'rect(0,0,0,0',
                                      whiteSpace: 'nowrap',
                                      border: 0
                                    }}
                                  >
                                    •
                                  </span>
                                  {item}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                        </Box>

                        <Divider sx={{ borderColor: '#C9CACC', mb: 2.5 }} />

                        {/* Quick Links */}
                        <Box>
                          <Typography
                            component="h3"
                            variant="h3"
                            id="quick-link-heading"
                            mb={1.5}
                            sx={{
                              color: '#003D78',
                              fontWeight: 700,
                              fontSize: { xs: '18px', sm: '19px', md: '20px' },
                              lineHeight: { xs: '26px', sm: '27px', md: '28px' }
                            }}
                          >
                            {json['access']['quick']}
                          </Typography>
                          <List
                            component="ul"
                            sx={{ p: 0, m: 0 }}
                            aria-labelledby="quick-link-heading"
                          >
                            {json['access']['quickLinks']?.map(
                              (item: any, idx: number) => (
                                <ListItem
                                  key={idx}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 0.5,
                                    gap: 1
                                  }}
                                >
                                  <Link
                                    href={
                                      item.download
                                        ? item.url
                                        : `/${i18n.language}${item.url}`
                                    }
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleQuickLinks(item.url, item.download);
                                    }}
                                    target={item.download ? '_blank' : ''}
                                    download={item.download}
                                    rel={
                                      item.download
                                        ? 'noopener noreferrer'
                                        : undefined
                                    }
                                    underline="hover"
                                    sx={{
                                      color: '#1460AA',
                                      fontSize: {
                                        xs: '14px',
                                        sm: '15px',
                                        md: '16px'
                                      },
                                      fontWeight: 700,
                                      lineHeight: {
                                        xs: '20px',
                                        sm: '22px',
                                        md: '24px'
                                      },
                                      ':hover': {
                                        color: '#003D78',
                                        textDecoration: 'underline'
                                      },
                                      ':focus-visible': {
                                        outline: '2px solid black',
                                        outlineOffset: '2px'
                                      },
                                      textDecoration: 'none',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: item.linkText
                                    }}
                                  />
                                  {item.download && (
                                    <span className="sr-only">
                                      (opens in new tab)
                                    </span>
                                  )}
                                  <NavigateNextRoundedIcon
                                    sx={{ fontSize: '16px', color: '#1460AA' }}
                                    aria-hidden="true"
                                  />
                                </ListItem>
                              )
                            )}
                          </List>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </section>
            </Grid>
            <Grid item width={'100%'}>
              <section>
                <Box
                  sx={{
                    paddingX: '0px'
                  }}
                >
                  {/* How to integrate card */}
                  <Card
                    sx={{
                      mb: 1,
                      borderRadius: '16px',
                      boxShadow: '0px 0px 15px 0px #00000026 !important',
                      px: { xs: 2, sm: 3, md: 4 },
                      py: { xs: 2, sm: 2.5, md: 3 }
                    }}
                  >
                    <Grid container alignItems="center">
                      {/* Left: Heading + description */}
                      <Grid
                        item
                        xs={12}
                        sm={isTablet ? 12 : 8}
                        md={isTablet ? 12 : 8}
                      >
                        <Box>
                          <Typography
                            component="h3"
                            variant="h3"
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '20px', sm: '22px', md: '24px' },
                              lineHeight: {
                                xs: '28px',
                                sm: '30px',
                                md: '32px'
                              },
                              color: '#000000',
                              mb: 1
                            }}
                          >
                            {json['access']['heading']}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 400,
                              fontSize: { xs: '14px', sm: '15px', md: '16px' },
                              lineHeight: {
                                xs: '20px',
                                sm: '22px',
                                md: '24px'
                              },
                              color: '#000000'
                            }}
                          >
                            {json['access']['sub']}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Right: Button */}
                      <Grid
                        item
                        xs={12}
                        sm={isTablet ? 12 : 4}
                        md={isTablet ? 12 : 4}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: isTablet
                              ? 'flex-start'
                              : {
                                  xs: 'flex-start',
                                  sm: 'flex-end',
                                  md: 'flex-end'
                                },
                            mt: isTablet ? 2 : { xs: 2, md: 0 }
                          }}
                        >
                          <Button
                            variant="primary"
                            size="md"
                            aria-label={json['getStarted']}
                            onClick={handleRegisterClick}
                          >
                            {json['login']}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                </Box>
              </section>
            </Grid>
            <Grid item>
              <section aria-label={json['section3']}>
                <Box
                  sx={{
                    mb: '60px'
                  }}
                >
                  <Grid
                    container
                    alignItems="center"
                    sx={{
                      mb: 3,
                      position: 'relative',
                      display: { xs: 'none', lg: 'flex' }
                    }}
                    role="presentation"
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 16,
                        right: 16,
                        borderTop: '2px dotted #C9CACC',
                        transform: 'translateY(-50%)',
                        width: '98%'
                      }}
                      aria-hidden="true"
                    />
                    <Grid
                      item
                      xs={12}
                      sx={{ display: 'flex', gap: '358px', zIndex: 1 }}
                      role="presentation"
                    >
                      {[1, 2, 3].map((n) => (
                        <Box
                          key={n}
                          sx={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: '#FFFFFF',
                            border: '2px solid #A7D0E3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center', // fontFamily: 'Open Sans',
                            fontWeight: 700,
                            fontSize: '20px',
                            color: '#003D78'
                          }}
                          aria-hidden="true"
                        >
                          {n}
                        </Box>
                      ))}
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    spacing={{ xs: 2, md: 3 }}
                    alignItems="stretch"
                    sx={{
                      position: { xs: 'relative', lg: 'static' },
                      pl: { xs: '56px', lg: 0 },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: isTablet ? 40 : { xs: 35, md: 45 },
                        top: 25,
                        bottom: 220,
                        borderLeft: '2px dotted #C9CACC',
                        display: { xs: 'block', lg: 'none' }
                      }
                    }}
                  >
                    {json['cards'].map((item, cardIdx) => (
                      <Grid
                        item
                        xs={12}
                        lg={4}
                        key={cardIdx}
                        sx={{
                          display: 'flex',
                          position: { xs: 'relative', md: 'static' }
                        }}
                      >
                        {/* Mobile/tablet step number circle */}
                        <Box
                          aria-hidden="true"
                          sx={{
                            display: { xs: 'flex', lg: 'none' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'absolute',
                            left: { xs: -40, md: 25 },
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: '#FFFFFF',
                            border: '2px solid #A7D0E3',
                            fontWeight: 700,
                            fontSize: '18px',
                            color: '#003D78',
                            zIndex: 1
                          }}
                        >
                          {cardIdx + 1}
                        </Box>
                        <Box
                          component="article"
                          aria-label={`Step ${cardIdx + 1}`}
                          sx={{
                            width: '100%',
                            backgroundColor: '#FFFFFF',
                            borderRadius: '16px',
                            border: '1px solid #78A1B4',
                            p: { xs: 3, md: 4 },
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <Box sx={{ mb: 2 }}>
                            <img
                              src={item.logo}
                              alt=""
                              role="presentation"
                              style={{
                                width: '72px',
                                height: '72px',
                                flexShrink: 0
                              }}
                            />
                          </Box>
                          <Typography
                            component="h4"
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '18px', md: '20px' },
                              lineHeight: { xs: '26px', md: '28px' },
                              color: '#000',
                              mb: 1.5
                            }}
                          >
                            {item.subHeader}
                          </Typography>
                          <ul
                            style={{ listStyle: 'none', margin: 0, padding: 0 }}
                          >
                            {item.details.map((text, i) => (
                              <li
                                key={i}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  marginBottom:
                                    i < item.details.length - 1 ? '8px' : 0
                                }}
                                aria-label={`Bullet. ${text}`}
                              >
                                <img
                                  src={Check}
                                  alt=""
                                  role="presentation"
                                  style={{
                                    width: '16px',
                                    height: '12px',
                                    marginRight: '8px',
                                    marginTop: '6px',
                                    flexShrink: 0
                                  }}
                                />

                                <Typography
                                  sx={{
                                    fontWeight: 400,
                                    fontSize: { xs: '14px', md: '16px' },
                                    lineHeight: { xs: '20px', md: '24px' },
                                    color: '#000'
                                  }}
                                >
                                  <span
                                    style={{
                                      position: 'absolute',
                                      width: '1px',
                                      height: '1px',
                                      padding: 0,
                                      margin: '-1px',
                                      overflow: 'hidden',
                                      clip: 'rect(0,0,0,0',
                                      whiteSpace: 'nowrap',
                                      border: 0
                                    }}
                                  >
                                    •
                                  </span>
                                  {text}
                                  {i === item.details.length - 1 && (
                                    <span className="sr-only">
                                      {' '}
                                      end of list
                                    </span>
                                  )}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                          {cardIdx === json['cards'].length - 1 && (
                            <span className="sr-only"> end of section</span>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </section>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default DirectIntegrationTab;
